import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { fetchHseEvents, deleteHseEvent } from "../../../../api/hse/events";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { HseEventFilters } from "../../../../components/dashboard/hse/hseEventFilters";
import { HseEventStatistics } from "../../../../components/dashboard/hse/hseEventStatistics";
import { PMDialog } from "../../../../components/utils/PMDialog";

/**
 * HSE/KS Events List - Overview of all events (ISO 9001:2015 compliant)
 * Shows all event types with filtering options
 */
export const HseKsEventsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    event_type: "",
    event_category: "",
    status: "",
    start_date: "",
    end_date: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [activeTab, setActiveTab] = useState(() => {
    const path = location.pathname;
    if (path.includes("/deviations")) return "deviations";
    if (path.includes("/near-misses")) return "near-misses";
    if (path.includes("/accidents")) return "accidents";
    if (path.includes("/complaints")) return "complaints";
    if (path.includes("/observations")) return "observations";
    return "all";
  });

  const tabs = [
    { id: "all", label: "Alle", path: "/dashboard/hse-ks/events" },
    { id: "deviations", label: "Avvik", path: "/dashboard/hse-ks/events/deviations" },
    { id: "near-misses", label: "Nestenulykker", path: "/dashboard/hse-ks/events/near-misses" },
    { id: "accidents", label: "Ulykker", path: "/dashboard/hse-ks/events/accidents" },
    { id: "complaints", label: "Reklamasjoner", path: "/dashboard/hse-ks/events/complaints" },
    { id: "observations", label: "Observasjoner", path: "/dashboard/hse-ks/events/observations" },
  ];

  // Map ISO event types to backend filters
  const getEventFilters = () => {
    const baseFilters = { ...filters };
    
    switch (activeTab) {
      case "deviations":
        // Avvik: law_violation, regulation_violation, procedure_violation
        baseFilters.event_category = "law_violation,regulation_violation,procedure_violation";
        break;
      case "near-misses":
        // Nestenulykker: near_miss event type
        baseFilters.event_type = "near_miss";
        break;
      case "accidents":
        // Ulykker: injury event type
        baseFilters.event_type = "injury";
        break;
      case "complaints":
        // Reklamasjoner: can use event_category "other" or a specific category
        baseFilters.event_category = "other";
        break;
      case "observations":
        // Observasjoner: information category
        baseFilters.event_category = "information";
        break;
      default:
        // All: no additional filters
        break;
    }
    
    return baseFilters;
  };

  const fetchEvents = async (page = 1) => {
    try {
      setLoading(true);
      const eventFilters = getEventFilters();
      const params = {
        ...eventFilters,
        page,
        per_page: pagination.per_page,
      };
      const response = await fetchHseEvents(params);
      setEvents(response.data || []);
      setPagination({
        ...pagination,
        page,
        total: response.meta?.total || 0,
        total_pages: response.meta?.total_pages || 0,
      });
    } catch (err) {
      console.error("Error fetching HSE events:", err);
      setError(true);
      toast.error(t("Kunne ikke laste hendelser"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [filters, activeTab]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page) => {
    fetchEvents(page);
  };

  const handleDeleteEvent = async (eventId) => {
    if (
      window.confirm(t("Er du sikker på at du vil slette denne hendelsen?"))
    ) {
      try {
        await deleteHseEvent(eventId);
        toast.success(t("Hendelse ble slettet"));
        fetchEvents(pagination.page);
      } catch (err) {
        toast.error(t("Kunne ikke slette hendelse"));
      }
    }
  };

  const getEventTypeIcon = (eventType) => {
    switch (eventType) {
      case "injury":
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case "near_miss":
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case "property_damage":
        return <XCircleIcon className="h-5 w-5 text-orange-500" />;
      case "environmental":
        return <ExclamationTriangleIcon className="h-5 w-5 text-green-500" />;
      default:
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case "draft":
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {t("Kladd")}
          </span>
        );
      case "submitted":
        return (
          <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
            {t("Innsendt")}
          </span>
        );
      case "under_review":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("Under vurdering")}
          </span>
        );
      case "closed":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("Lukket")}
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  const getSeverityBadge = (severity) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (severity) {
      case "low":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("Lav")}
          </span>
        );
      case "medium":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("Medium")}
          </span>
        );
      case "high":
        return (
          <span className={`${baseClasses} bg-orange-100 text-orange-800`}>
            {t("Høy")}
          </span>
        );
      case "critical":
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            {t("Kritisk")}
          </span>
        );
      default:
        return null;
    }
  };

  const headerActions = [
    {
      component: null,
      label: t("Statistikk"),
      action: () => setShowStatistics(true),
      icon: CheckCircleIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
  ];

  if (loading && events.length === 0) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste hendelser")]} />;
  }

  return (
    <>
      <DashboardHeader
        title="Hendelser"
        subtitle="Oversikt over alle hendelser (ISO 9001:2015 - kap. 10 Forbedring)"
        actions={headerActions}
        mainButton={{
          label: t("Ny hendelse"),
          action: () => navigate("/dashboard/hse-ks/events/new"),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            {/* Tabs */}
            <div className="bg-white shadow sm:rounded-lg">
              <div className="border-b border-gray-200 px-4 py-3 sm:px-6">
                <nav className="flex space-x-4" aria-label="Tabs">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        navigate(tab.path);
                      }}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Filters Section */}
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder={t("Søk i hendelser...")}
                        value={filters.search}
                        onChange={(e) =>
                          handleFilterChange({ ...filters, search: e.target.value })
                        }
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <FunnelIcon className="h-4 w-4 mr-2" />
                      {t("Filtre")}
                    </button>
                  </div>
                </div>

                {showFilters && (
                  <HseEventFilters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClose={() => setShowFilters(false)}
                  />
                )}
              </div>
            </div>

            {/* Events List */}
            <div className="bg-white shadow sm:rounded-lg">
              {!events || events.length === 0 ? (
                <div className="px-4 py-5 sm:p-6">
                  <ResourceEmpty message={t("Ingen hendelser funnet")} />
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {events.map((event) => (
                    <li key={event.id} className="px-6 py-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="flex-shrink-0">
                            {getEventTypeIcon(event.event_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <Link
                                to={`/dashboard/hse-ks/events/${event.id}`}
                                className="text-sm font-medium text-gray-900 truncate hover:text-blue-600"
                              >
                                {event.title}
                              </Link>
                              {getStatusBadge(event.status)}
                              {getSeverityBadge(event.severity_level)}
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <span>{event.event_type_display}</span>
                              <span>
                                {new Date(event.event_date).toLocaleDateString()}
                              </span>
                              <span>{event.location}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/dashboard/hse-ks/events/${event.id}`}
                            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            {t("Vis")}
                          </Link>
                          {event.can_be_edited && (
                            <Link
                              to={`/dashboard/hse-ks/events/${event.id}/edit`}
                              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              <PencilIcon className="h-4 w-4 mr-1" />
                              {t("Rediger")}
                            </Link>
                          )}
                          {event.can_be_edited && (
                            <button
                              onClick={() => handleDeleteEvent(event.id)}
                              className="inline-flex items-center px-2.5 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              <TrashIcon className="h-4 w-4 mr-1" />
                              {t("Slett")}
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="bg-white shadow sm:rounded-lg px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    {t("Viser")} {(pagination.page - 1) * pagination.per_page + 1} -{" "}
                    {Math.min(
                      pagination.page * pagination.per_page,
                      pagination.total
                    )}{" "}
                    {t("av")} {pagination.total} {t("resultater")}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("Forrige")}
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.total_pages}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("Neste")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DashboardHeader>

      {/* Statistics Dialog */}
      <PMDialog
        open={showStatistics}
        onClose={() => setShowStatistics(false)}
        title={t("Hendelsesstatistikk")}
        size="4xl"
      >
        <HseEventStatistics onClose={() => setShowStatistics(false)} />
      </PMDialog>
    </>
  );
};

